<template>
<div class="row">
	<div class="col-12">
		<partial-loader :loading="isFetching"></partial-loader>
	</div>

	<div class="col-12"
	     v-if="!isFetching">


		<h2 class="display-4">{{ $t('buyerCost.bought') }}</h2>

		<b-alert v-if="error"
		         variant="danger"
		         show>{{ error }}</b-alert>


		<b-table v-if="!isFetching"
				 
				striped
				hover
				show-empty
				:items="bids.data"
				:fields="fields"
				:per-page="parseInt(bids.per_page)"
				:sort-by.sync="sortBy"
				:sort-desc.sync="sortDesc"
				:busy="isFetching"
				:empty-text="$t('buyerCost.noBids')">

			<template v-slot:cell(auction_order_number)="row">
			 	<span>{{ row.item.item.auction_order_number }}</span>
			</template>

			<template v-slot:cell(number)="row">
			 	<span>{{ row.item.item.seller.code }}-{{ row.item.item.number }}</span>
			</template>

			<template v-slot:cell(name)="row">
			 	{{ row.item.item.title }}
			</template>

			<template v-slot:cell(price)="row">
			 	<div class="text-right">&euro; {{ Math.round(row.item.price) }}</div>
			</template>

			<template v-slot:cell(paid)="row">
					  <b-btn-group class="pull-right">

	  					<b-btn variant="outline-primary"
	  						   :to="{ name: 'item', params: { itemId: row.item.item.id } }"
	  						   v-b-tooltip.hover
	  						   :title="$t('item.watch')">
	  						<fa-icon icon="eye"
	  								 fixed-width />
	  					</b-btn>

	  					<b-btn v-if="row.item.item.paid"
	  						   variant="success"
	  						   @click="setPaid(row.item.item.id, 0)"
	  						   v-b-tooltip.hover
	  						   :title="$t('item.markUnpaid')">
	  						<fa-icon icon="check"
	  								 fixed-width />
	  					</b-btn>

	  					<b-btn v-if="!row.item.item.paid"
	  						   variant="outline-success"
	  						   @click="setPaid(row.item.item.id, 1)"
	  						   v-b-tooltip.hover
	  						   :title="$t('item.markPaid')">
	  						<fa-icon icon="check"
	  								 fixed-width />
	  					</b-btn>

	   				</b-btn-group>
			</template>

		</b-table>

		<div class="row">

			<div class="col-sm-12 col-md-6">
				<p class="font-italic"
				   v-if="bids.data.length > 0">
					{{ $t('item.markAll') }}
					<b-button-group size="sm">
						<b-button variant="outline-danger"
						          @click="setItemsPaymentStatus(0)">{{ $t('item.markAllUnpaid') }}</b-button>
						<b-button variant="outline-success"
						          @click="setItemsPaymentStatus(1)">{{ $t('item.markAllPaid') }}</b-button>

					</b-button-group>
				</p>
			</div>

			<div class="col-sm-12 col-md-6">
				<b-pagination-nav :link-gen="linkGen"
				                  :number-of-pages="bids.last_page"
				                  :value="bids.current_page"
				                  use-router
				                  align="right" />

			</div>
		</div>
	</div>
</div>
</template>


<script>
import { isNil } from 'lodash';
import BidService from '@/services/bid-service';
import ItemService from '@/services/item-service';
import PartialLoader from '@/components/elements/PartialLoader';
import { flashSuccess, flashDanger } from '@/helpers/flash-message-helper';
import { isCancel, CancelToken } from 'axios';
import { smallDate } from '@/filters/date-filters';

export default {
	components: {
		PartialLoader
	},
	data() {
		return {
			isFetching: true,
			bids: false,
			error: false,
			fields: [
				{ key: 'auction_order_number', label: this.$t('item.auctionOrderNumber') },
				{ key: 'number', label: this.$t('item.itemNumber') },
				{ key: 'name', label: this.$t('common.item') },
				{ key: 'price', label: this.$t('bid.price') },
				{ key: 'paid', label: this.$t('common.actions') }
			],
			currentPage: 1,
			sortBy: 'id',
			sortDesc: true,
		}
	},
	watch: {
		'$route': 'fetchBids'
	},
	methods: {
		fetchBids() {
			this.isFetching = true;

			const query = this.$route.query;
			const currentPage = Number.parseInt(query.page);
			this.currentPage = isNaN(currentPage) ? 1 : currentPage;

			if (!isNil(this.cancelToken)) {
				this.cancelToken.cancel();
			}

			this.cancelToken = CancelToken.source();

			const params = {
				page: this.currentPage,
				sort: this.sortBy,
				order: this.sortDesc ? 'desc' : 'asc'
			}

			return BidService.getAuctionBidNumberBids(this.$route.params.auctionId, this.$route.params.bidNumber, params, this.cancelToken.token).then(response => {
				this.isFetching = false;
				this.bids = response;
			}).catch(error => {
				if (!isCancel(error)) {
					this.isFetching = false;
					this.error = error.message;
				}
			});
		},
		setPaid(itemId, paid) {
			this.isFetching = true;

			return ItemService.updateOne(itemId, { paid: paid }).then(response => {
				this.isFetching = false;
				if (paid) {
					flashSuccess(this.$t('item.paidSuccess'));
				} else {
					flashSuccess(this.$t('item.unPaidSuccess'));
				}
				this.fetchBids();
			}).catch(error => {
				if (!isCancel(error)) {
					this.isFetching = false;
					this.error = error.message;
				}
			});
		},
		setItemsPaymentStatus(paid) {
			this.isFetching = true;

			return ItemService.setBuyerItemsPaymentStatus(this.$route.params.auctionId, this.$route.params.bidNumber, { paid: paid }).then(response => {
				this.isFetching = false;
				if (paid) {
					flashSuccess(this.$t('item.allItemsPaidSuccess'));
				} else {
					flashSuccess(this.$t('item.allItemsUnpaidSuccess'));
				}
				this.fetchBids();
			}).catch(error => {
				if (!isCancel(error)) {
					this.isFetching = false;
					this.error = error.message;
				}
			});
		},
		linkGen(pageNumber) {
			return `?page=${pageNumber}`;
		}
	},
	mounted() {
		this.fetchBids();
	}
}
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// BidsList.vue?259831cc